import {
  Box,
  SimpleGrid,
  InputRightElement,
  IconButton,
  Icon,
  Divider,
  Heading,
  Text,
  Image,
  Link,
} from '@chakra-ui/react';
import { Formik, Form } from 'formik';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { IconViewOff, IconView } from 'assets/icons';
import { signupSaying } from 'assets/images';
import Button from 'components/Button';
import { FormikInput, FormikSelect } from 'components/Formik';
import PasswordStrengthChecker from 'components/PasswordStrengthChecker';
import { routePaths } from 'global/routePaths';
import toaster from 'utils/toaster';

import { jobTitleOptions } from '../option';
import { initialPersonalDetailsData, personalDetailsValidationSchema } from './schema';

interface IPersonalDetails {
  handlePersonalDetailsFormSubmit: (value: typeof initialPersonalDetailsData) => void;
}

const PersonalDetails: React.FC<IPersonalDetails> = ({ handlePersonalDetailsFormSubmit }) => {
  const { t } = useTranslation(['auth', 'common']);
  const passwordInputRef = useRef<any>(null);

  const handleClickShowPassword = (setFieldValue: Function, showPassword: boolean) => {
    setFieldValue('showPassword', !showPassword);
  };

  const handleJobTitleCallback = (selected: any) => {
    if (selected?.value === 'Employee') {
      toaster.info({
        title: 'Please reach out to your Timeero account administrator to create your account.',
      });
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      p={{
        base: '32px 16px',
        md: '60px',
        lg: '32px',
        xl: '60px',
        '2xl': '100px',
      }}
    >
      <Box>
        <Heading as="h2" size="3xl" mb="2">
          {t('auth:valueTime')} <br />
          {t('auth:boostProductivity')}
        </Heading>
        <Text fontSize="md" fontWeight="semibold" color="gray.500" mb="9">
          {t('auth:start14DayTrial')}
        </Text>
        <Box maxWidth="500px" width="100%">
          <Formik
            initialValues={initialPersonalDetailsData}
            validationSchema={personalDetailsValidationSchema}
            onSubmit={handlePersonalDetailsFormSubmit}
          >
            {({ values, setFieldValue }) => (
              <Form>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                  <FormikInput type="text" name="firstName" label={t('auth:firstName')} />
                  <FormikInput type="text" name="lastName" label={t('auth:lastName')} />
                </SimpleGrid>
                <FormikInput type="text" name="email" label={t('auth:emailAddress')} />
                <FormikInput
                  type="text"
                  name="message"
                  label={t('common:message')}
                  inputProps={{ autoComplete: 'off' }}
                  formControlProps={{
                    opacity: 0,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: 0,
                    width: 0,
                    zIndex: -1,
                  }}
                />
                <FormikSelect
                  name="job_title"
                  label={t('auth:jobTitle')}
                  options={jobTitleOptions}
                  formControlProps={{ isRequired: true }}
                  selectProps={{
                    placeholder: 'Select your job title',
                    isClearable: false,
                  }}
                  onChangeCallback={handleJobTitleCallback}
                />
                <Box position="relative">
                  <FormikInput
                    type={values.showPassword ? 'text' : 'password'}
                    inputProps={{ ref: passwordInputRef }}
                    name="password"
                    label={t('auth:password')}
                    inputElement={
                      <InputRightElement width="4.5rem">
                        <IconButton
                          aria-label="toggle view password"
                          size="sm"
                          onClick={() => handleClickShowPassword(setFieldValue, values.showPassword)}
                          icon={values.showPassword ? <Icon as={IconViewOff} /> : <Icon as={IconView} />}
                        ></IconButton>
                      </InputRightElement>
                    }
                  />
                  <PasswordStrengthChecker
                    passwordValue={values.password}
                    listProps={{
                      visibility: passwordInputRef?.current === document.activeElement ? 'visible' : 'hidden',
                      opacity: passwordInputRef?.current === document.activeElement ? 100 : 0,
                    }}
                  />
                </Box>
                <Button
                  type="submit"
                  colorScheme="secondary"
                  width="100%"
                  size="lg"
                  disabled={values.job_title?.value === 'Employee'}
                >
                  {t('auth:letsDoThis')}
                </Button>
              </Form>
            )}
          </Formik>
          <Image src={signupSaying} alt="No credit card required. Cancel Anytime." mt="4" />
          <Divider colorScheme="gray" borderStyle="dashed" mt="8" mb="6" />
          <Text fontSize="sm" color="gray.500">
            {t('auth:alreadyHaveTimeeroAccount')}
            <Link as={RouterLink} to={routePaths.auth.login} color="primary.200" ml="1">
              {t('auth:logIn')}
            </Link>
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default PersonalDetails;
